
<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import HeaderInfoText from '@/components/HeaderInfoText'
import store from '@/store/store'
import { getSimpleDate } from '@/utils/helpers/FormatDate'
import { positivePayMixins } from '@/Mixins/PositivePay'
import { aggregateMixins } from '@/Mixins/Aggregate'
import { reportMixins } from '@/Mixins/Reports'
import { AgGridVue } from 'ag-grid-vue'
import NachaTransactionsList from '@/components/SigmaClientNew/NachaTransactionsList.vue'
import NachaPaymentConfList from '@/components/SigmaClientNew/NachaPaymentConfList.vue'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridAccountsPayableColumns from '@/Mixins/SigmaClientNew/AgGridAccountsPayableColumns'
import { VendorResource, AccountsPayableResource } from '@sigmacloud/sigma-client/dist/resources'

export default Vue.extend({
  name: 'NachaExport',
  mixins: [positivePayMixins, aggregateMixins, reportMixins, AgGridMethods, AgGridAccountsPayableColumns],
  components: {
    HeaderInfoText,
    NachaTransactionsList,
    NachaPaymentConfList,
    AgGridVue
  },
  props: {
    paymentBank: String || null,
    paymentLines: Array,
    startingPaymentConfNumber: String || null
  },
  data () {
    return {
      reportOptions: {    // Options to run report.
        format: 'cnb',        // ACH Format
        payment_date: getSimpleDate(new Date(), 'yyyy-mm-dd')
      },
      allCodes: {         // Show codes, e.g. accounts, series, locations, sets, etc.
        bank__in: []
      },
      selectedCodes: {    // User selected codes from choices of allCodes above
        bank__in: []
      },
      disableReportRun: false, // Disable run report button if false
      loading: false,
      // selectedOption: 'all',  // Future functionality
      prettyJson: undefined,
      reportOutputFormats: [
        { value: 'cnb', label: 'City National Bank', disabled: false }
      ],
      step: 1,
      ccMail: [],
      iteration: 0,
      gridApi: null,
      columnApi: null,
      hideVendorAccountInfo: true,
      columnDefsNacha: undefined,
      columnDefsUpdate: undefined,
      gridOptionsNacha: undefined,
      gridOptionsUpdate: undefined,
      rowData: [],
      totalAvailable: undefined,
      totalSelected: undefined,
      selectedResources: [],
      simulatedTransactions: [],
      export_id: null,
      reportEndpoint: 'payments/register/',  // excluding "reports/" prefix to allow use with function startRenderingReport
      vendorPayments: [],
      paymentConfUpdatedRows: [],
      nextPaymentConfirmationNumber: null,
      validatingDuplicates: false,
      loadingRelated: false,
      pdfLoader: false,
      paymentInfo: [],
      filteredselectedResources: []
    }
  },
  methods: {
    /**
     * Performs basic validation, e.g. are required fields selected
     * @return {boolean}
     */
    checkRequiredElementFields () {
      /* Add "required" to el-select and "required-multiselect" to multiple selectors, e.g.
        Single select:
          <el-select v-model="bank" :multiple="false" placeholder="Bank Account" required>
        Multiple select:
          <el-select v-model="company" :multiple="true"  placeholder="All" required-multiselect>
      */
      let errorCount = 0
      const requiredInputFields = this.$el.querySelectorAll('[required] input')             // Works for el-select (non-multiple)/input tags
      for (const field of requiredInputFields) {
        field.classList.remove('form-error')
        if (field && !field.value) {
          field.classList.add('form-error')
          errorCount++
        }
      }
      const requiredMultiSelectFields = this.$el.querySelectorAll('[required-multiselect]') // Need this for multiple-select el-select boxes
      for (const field of requiredMultiSelectFields) {
        field.querySelector('input').classList.remove('form-error')
        if (field && field.querySelectorAll('.el-select__tags-text').length === 0) {
          field.querySelector('input').classList.add('form-error')
          errorCount++
        }
      }
      return !errorCount
    },
    /**
     * Finds matching checks and APs to export, and then get thte data to format into the proper formats.
     * @return {Promise<boolean>}
     */
    async getTransactions () {
      if (!this.checkRequiredElementFields() && (!this.paymentBank || (this.selectedBank && !(this.selectedBank.branch_number)))) {
        return false
      }
      this.loading = true
      this.disableReportRun = true

      try {
        // Get Check/AP Records
        if (this.paymentBank) {
          this.rowData = await this.getExportableACH(this.selectedBank, null, this.paymentLines.map((ap) => ap.id))
        } else {
          this.rowData = await this.getExportableACH(this.selectedBank)
        }
        this.paymentConfUpdatedRows = []
        this.nextPaymentConfirmationNumber = null
        this.assignPaymentConfirmationNumbers()
        this.iteration++
        this.step = 2
      } catch (error) {
        console.log(error)
        return error
      } finally {
        this.loading = false
        this.disableReportRun = false
      }
    },
    assignPaymentConfirmationNumbers () {
      let next = (this.nextPaymentConfirmationNumber !== null)
      let nextPaymentConfirmationNumber = this.nextPaymentConfirmationNumber ? this.nextPaymentConfirmationNumber : this.startingPaymentConfNumber
      let paymentConfirmationNumber = ''
      this.rowData.map((rowData) => {
        if (!rowData.attributes.payment_confirmation_number) {
          const paymentVendor = this.paymentConfUpdatedRows.find((v) => v.vendor === rowData.attributes.vendor)
          if (paymentVendor) {
            paymentConfirmationNumber = paymentVendor.payment_confirmation_number
            this.paymentConfUpdatedRows.push({
              id: rowData.attributes.id,
              vendor: rowData.attributes.vendor,
              payment_confirmation_number: paymentConfirmationNumber
            })
            rowData.set('payment_confirmation_number', paymentConfirmationNumber)
          } else {
            paymentConfirmationNumber = this.getNextPaymentNumber(nextPaymentConfirmationNumber, next)
            nextPaymentConfirmationNumber = paymentConfirmationNumber
            next = true
            if (this.rowData.filter((p) => String(p.attributes.payment_confirmation_number) === String(paymentConfirmationNumber)).length) {
              this.nextPaymentConfirmationNumber = paymentConfirmationNumber
              this.assignPaymentConfirmationNumbers()
            } else {
              this.paymentConfUpdatedRows.push({
                id: rowData.attributes.id,
                vendor: rowData.attributes.vendor,
                payment_confirmation_number: paymentConfirmationNumber
              })
              rowData.set('payment_confirmation_number', paymentConfirmationNumber)
            }
          }
        }
      })
    },
    getNextPaymentNumber (paymentConfirmationNumber, next) {
      if (next) {
        let numericPart = parseInt(paymentConfirmationNumber.match(/\d+/g))
        let nextPaymentNumber = numericPart + 1
        let reg = new RegExp(numericPart)
        return paymentConfirmationNumber.replace(reg, nextPaymentNumber)
      } else {
        return paymentConfirmationNumber
      }
    },
    goToConfirmationPage () {
      this.validatingDuplicates = true
      this.simulatedTransactions = this.$refs['nacha_transactions_list'].simulatedTransactionData
      this.selectedResources = this.$refs['nacha_transactions_list'].gridApi.getSelectedRows()
      const selectedPayments = this.$store.state.payment.selection
      const selectedIds = new Set(selectedPayments.map(item => item.id))
      this.filteredselectedResources = this.selectedResources.filter(item => selectedIds.has(item.attributes.id))
      if (this.simulatedTransactions.length === 0) {
        this.createNotification('Error', 'Please select transactions to export.', 'warning', null, null, null, 5000)
        this.simulatedTransactions = undefined
        this.validatingDuplicates = false
        return false
      } else {
        if (this.paymentNumberError()) {
          this.validatingDuplicates = false
          return
        }
        const paymentConfirmationNumber = this.startingPaymentConfNumber
        let paymentConfirmationNumbers = this.selectedResources.map((v) => String(v.attributes.payment_confirmation_number))
        let transactionIds = this.selectedResources.map((v) => v.attributes.id)
        let apPayload = {
          payment_confirmation_number__in: [...new Set(paymentConfirmationNumbers)],
          bank_id: this.selectedBank.id
        }
        if (this.selectedResources[0].attributes && this.selectedResources[0].attributes.batch) {
          apPayload.batch = this.selectedResources[0].attributes.batch
        }
        this.$store.dispatch('fetchApsWithQuery', apPayload).then((response) => {
          this.startingPaymentConfNumber = paymentConfirmationNumber
          let duplicatePaymentConfNumbers = []
          if (response.count > 0) {
            Object.values(response.entities.aps).forEach((ap) => {
              if (!transactionIds.includes(ap.id)) {
                let rows = this.selectedResources.filter((v) => String(v.attributes.payment_confirmation_number) === String(ap.payment_confirmation_number))
                rows.forEach((row) => {
                  if (!duplicatePaymentConfNumbers.find((v) => v.id === row.attributes.id)) {
                    duplicatePaymentConfNumbers.push({
                      id: row.attributes.id,
                      number: row.attributes.number,
                      payment_confirmation_number: String(row.attributes.payment_confirmation_number)
                    })
                  }
                })
              }
            })
          }
          return {count: response.count, duplicates: duplicatePaymentConfNumbers}
        }).then((response) => {
          if (response.count > 0 && response.duplicates.length > 0) {
            const duplicates = [...new Set(response.duplicates.map((v) => v.payment_confirmation_number))]
            this.createConfirmationBox('Confirm', `Do you want to continue with duplicate payment numbers ${duplicates.join(', ')}`)
              .then(confirmation => {
                if (confirmation === true) {
                  this.continueToConfirmationPage()
                } else {
                  this.validatingDuplicates = false
                }
              })
          } else {
            this.continueToConfirmationPage()
          }
        })
      }
    },
    continueToConfirmationPage () {
      let promises = []
      if (this.paymentConfUpdatedRows.length) {
        this.paymentConfUpdatedRows.forEach((row) => {
          let selectedRow = this.selectedResources.find((resource) => Number(row.id) === Number(resource.attributes.id))
          if (selectedRow) {
            const id = selectedRow.attributes.id
            const headerData = {
              payment_confirmation_number: selectedRow.attributes.payment_confirmation_number,
              payment_confirmation_number_duplicate_accepted: true
            }
            promises.push(this.$store.dispatch(`updateAp`, { id, data: headerData }))
          }
        })
        if (promises.length) {
          Promise.all(promises)
            .then(r => {
              this.validatingDuplicates = false
              this.step = 3
            })
            .catch(e => {
              console.log(e)
              this.validatingDuplicates = false
            })
        } else {
          this.validatingDuplicates = false
          this.step = 3
        }
      } else {
        this.validatingDuplicates = false
        this.step = 3
      }
    },
    /**
     * Confirmation Dialog box
     * @param title
     * @param message
     * @param type
     * @return {Promise}
     */
    createConfirmationBox (title, message, type) {
      return new Promise((resolve, reject) => {
        if (!message) message = 'Confirm Action?'
        const newEl = this.$createElement
        message = newEl('p', null, [
          newEl('span', null, message + ' '),
          newEl('button',
            {
              attrs: { class: 'dialog-button' },
              on: {
                click: () => {
                  this.notificationBox.close()
                  return resolve(false)
                }
              }
            },
            'No' // Button label
          ),
          newEl('button',
            {
              attrs: { class: 'dialog-button button-success' },
              on: {
                click: () => {
                  this.notificationBox.close()
                  return resolve(true)
                }
              }
            },
            'Yes' // Button label
          )
        ])
        this.notificationBox = this.$message({
          title: title || 'Message',
          type: type || 'info',
          duration: 0,
          message,
          showClose: true
        })
      })
    },
    /**
     * Generates the Payment Register report. First, caches parameters, gets async report and hands off
     * to Ian's report builder
     * @return {Promise<boolean>}
     */
    async generatePaymentReport () {
      this.loading = true
      this.disableReportRun = true
      const data = { export_id: this.export_id }
      try {
        const resp = await this.$store.dispatch('cacheParams', { data })
        if (!(resp && resp.id)) {
          throw new Error('Error caching parameters for payment register report')
        }
        const { id, reportId } = resp
        const payload = { query: { cached_parameters: id, report_template: 'default' }, type: this.reportEndpoint, reportId }
        this.$store.dispatch('startRenderingReport', payload)
      } catch (e) {
        console.log(e)
        return e
      } finally {
        this.loading = false
        this.disableReportRun = false
      }
    },

    handleSelected (selection) {
      this.ccMail = selection
    },

    handleCellUpdate (param) {
      if (!param.newValue) {
        this.createNotification('Error', 'A valid email address must be entered.', 'warning', null, null, null, 5000)
        return
      }
      let index = this.ccMail.findIndex((mail) => mail.attributes.account === param.data.attributes.account)
      this.ccMail[index].attributes.cc_mail = param.newValue
    },

    async onNotify () {
      this.disableReportRun = true
      let exportPayload = []
      let selectedVendors = this.$refs['nacha_payment_conf_list'].simulatedPaymentTransactionData

      selectedVendors.forEach(vendor => {
        let data = this.ccMail.find((mail) => {
          return mail.attributes.vendor === vendor.attributes.vendor
        })
        let payload = {}
        Object.assign(payload, {vendor: vendor.attributes.vendor}, {aps: vendor.attributes.resources.map((ap) => ap.attributes.id)}, {bank_id: this.selectedBank.id}, {cc_mail: data.attributes.cc_mail})
        exportPayload.push(payload)
      })

      if (!selectedVendors.length) {
        this.createNotification('Error', 'Please select vendor to notify.', 'warning', null, null, null, 5000)
        return false
      }

      try {
        let notifyResource = VendorResource.wrap('/notify/achexport')
        let result = await notifyResource.post({ exportPayload })

        let offset = 0                                                                                  // Set offset so that notifs don't overlap each other
        if (result && result.data && result.data.error && result.data.error.vendors && result.data.error.vendors.length) {
          this.$notify({ title: 'Error', message: `Email not found for ${result.data.error.vendors.map((v) => v.name).join(', ')}`, type: 'error', offset })
        }
        if (result && result.data && result.data.success && result.data.success.vendors && result.data.success.vendors.length) {
          offset = 100
          this.$notify({ title: 'Success', message: `Email sent to vendor ${result.data.success.vendors.map((v) => v.name).join(', ')}`, type: 'success', offset })
        }
        this.disableReportRun = false
        return result
      } catch (error) {
        return error
      }
    },

    async onGenerateExport () {
      if (!this.selectedResources.length) {
        this.createNotification('Error', 'Please select transactions to export.', 'warning', null, null, null, 5000)
        return false
      }
      if (!this.validatePaymentDate(this.reportOptions.payment_date)) {
        return false
      }
      // if (this.achEnabled) {
      //   this.createNotification('Error', 'Payment instruction has been successfully sent to the bank.', 'success', null, null, null)
      // }
      this.loading = true
      this.disableReportRun = true
      let result
      let formatter, extension
      const verbose = false
      try {
        // Which Format
        switch (this.reportOptions.format) {
          case 'cnb':
            formatter = 'createAchCnb'
            extension = 'txt'
            break

          case 'jpmc':
            formatter = 'createAchJpmc'
            extension = 'txt'
            break

          case 'union':
            formatter = 'createAchUnion'
            extension = 'csv'
            break

          default:
            formatter = 'createAchCnb'
            extension = 'txt'
        }
        if (this.achEnabled) {
          const data = await this[formatter](this.simulatedTransactions, this.selectedBank, verbose, this.reportOptions.payment_date)
          this.prettyJson = data
        }
      } catch (error) {
        console.log(error)
        return error
      } finally {
        this.loading = false
        this.disableReportRun = false
        // Mark transactions as exported
        result = await this.markExportedACH(this.selectedResources, this.selectedBank, this.reportOptions.payment_date)
        this.export_id = result.id
        if (result) {
          let paymentResult = await this.$store.dispatch('createPayment', { export: result.id, paymentDates: this.reportOptions.payment_date })
          if (this.achEnabled && paymentResult.status === 'success') {
            this.createNotification('Error', 'Payment instruction has been successfully sent to the bank.', 'success', null, null, null)
          } else if (this.achEnabled && paymentResult.status === 'failed') {
            this.createNotification('Success', 'File is Ready. ', 'success', false, extension, 'nacha-' + this.selectedBank.code + '-' + getSimpleDate(new Date()))
          }
          this.vendorPayments = paymentResult.payments
        }
        this.step = 4
      }

      return result
    },
    validatePaymentDate (event) {
      let date = event.target ? event.target.value : event
      let validDate = getSimpleDate(date, 'yyyy-mm-dd')
      if (validDate === 'NaN-NaN-NaN') {
        this.$message({ showClose: false, message: 'Valid date is required', type: 'error' })
        return false
      } else {
        this.reportOptions.payment_date = validDate
        return true
      }
    },

    resetPage () {
      this.step = 1
      this.rowData = []
      this.totalAvailable = undefined
      this.totalSelected = undefined
      this.selectedResources = []
      this.simulatedTransactions = []
      this.$emit('finishPayment')
    },

    onUpdateTotals (event) {
      this.totalAvailable = event.totalAvailable
      this.totalSelected = event.totalSelected
    },

    onAchErrors (data) {
      const uniqueErrors = data.filter((obj, index, self) =>
        index === self.findIndex((t) => t.name === obj.name)
      )
      uniqueErrors.forEach((uniqueError) => {
        if (uniqueError.message) {
          this.createNotification('Error', uniqueError.message, 'warning')
        }
      })
    },

    updateFormat () {
      if (this.selectedCodes.bank__in) {
        if (this.selectedBank && this.selectedBank.ach_algorithm && this.selectedBank.ach_algorithm.match('JPMC')) {
          this.reportOutputFormats[0].value = 'jpmc'
          this.reportOutputFormats[0].label = 'JP Morgan Chase'
          this.reportOptions.format = 'jpmc'
        }
      }
    },
    simulatedAllTransactionData () {
      let transactions = []
      const groupedTransactions = this.rowData.reduce((attributes, ap) => {
        if (ap.attributes.vendor in attributes) {
          attributes[ap.attributes.vendor].attributes.push(ap)
        } else {
          attributes[ap.attributes.vendor] = ({ attributes: [ap] })
        }
        return attributes
      }, ({}))
      Object.values(groupedTransactions).forEach(row => {
        const vendorAttributes = []
        row.attributes.forEach(attribute => {
          vendorAttributes.push({attributes:
          {
            account: attribute.get('vendor.ach_account_number'),
            amount_decimal: attribute.get('amount_decimal'),
            description: attribute.get('description'),
            email: attribute.get('vendor.email'),
            payment_confirmation_number: attribute.get('payment_confirmation_number'),
            payment_date: this.reportOptions.payment_date,
            resources: [attribute],
            routing: attribute.get('vendor.ach_routing_number'),
            vendor: attribute.get('vendor.id'),
            vendor_name: attribute.get('vendor.name')
          }})
        })
        vendorAttributes[0].attributes.amount_decimal = String(vendorAttributes.reduce((a, b) => { return a + parseFloat(b.attributes['amount_decimal']) }, 0))
        transactions.push(vendorAttributes[0])
      })
      return transactions
    },
    setPaymentBank () {
      if (this.paymentBank) {
        this.step = 1
        this.rowData = []
        this.selectedCodes.bank__in = this.paymentBank
        this.updateFormat()
        if (this.$refs.getTransactions) {
          this.$refs.getTransactions.$el.click()
        }
      }
    },
    updatePaymentConfNumbersOnStep2 () {
      this.nextPaymentConfirmationNumber = null
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.processPaymentConfNumbersOnStep2([])
      }, 1000)
    },
    processPaymentConfNumbersOnStep2 (paymentConfirmationUpdatedRows) {
      let next = (this.nextPaymentConfirmationNumber !== null)
      let nextPaymentConfirmationNumber = this.nextPaymentConfirmationNumber ? this.nextPaymentConfirmationNumber : this.startingPaymentConfNumber
      let paymentConfirmationNumber = ''
      this.rowData.map((rowData, i) => {
        if (this.paymentConfUpdatedRows.find((v) => v.id === rowData.attributes.id)) {
          const paymentVendor = paymentConfirmationUpdatedRows.find((v) => v.vendor === rowData.attributes.vendor)
          if (paymentVendor) {
            paymentConfirmationNumber = paymentVendor.payment_confirmation_number
            paymentConfirmationUpdatedRows.push({
              id: rowData.attributes.id,
              vendor: rowData.attributes.vendor,
              payment_confirmation_number: paymentConfirmationNumber
            })
            rowData.set('payment_confirmation_number', paymentConfirmationNumber)
          } else {
            paymentConfirmationNumber = this.getNextPaymentNumber(nextPaymentConfirmationNumber, next)
            nextPaymentConfirmationNumber = paymentConfirmationNumber
            next = true

            if (this.rowData.filter((p) => String(p.attributes.payment_confirmation_number) === String(paymentConfirmationNumber)).length) {
              this.nextPaymentConfirmationNumber = paymentConfirmationNumber
              this.processPaymentConfNumbersOnStep2(paymentConfirmationUpdatedRows)
            } else {
              paymentConfirmationUpdatedRows.push({
                id: rowData.attributes.id,
                vendor: rowData.attributes.vendor,
                payment_confirmation_number: paymentConfirmationNumber
              })
              rowData.set('payment_confirmation_number', paymentConfirmationNumber)
            }
          }
        }
        if (this.rowData.length === (i) + 1) {
          this.paymentConfUpdatedRows = paymentConfirmationUpdatedRows
        }
      })
      this.$refs['nacha_transactions_list'].gridApi.refreshCells()
    },
    async updatePaymentNumber (params) {
      if (this.step >= 3) {
        let numbers = params.newValue.split(',')
        numbers.forEach((number, i) => {
          if (params.data.attributes.resources[i] && params.data.attributes.resources[i].attributes.payment_confirmation_number !== number.trim()) {
            params.data.attributes.resources[i].set('payment_confirmation_number', number.trim())
            params.data.attributes.resources[i].save()
          }
        })
      } else {
        let vendor = this.vendorPayments.find((v) => v.vendor_id === params.data.attributes.vendor)
        await this.$store.dispatch('updatePaymentNumber', { id: vendor.payment_id, number: params.newValue })
      }
    },
    paymentNumberError () {
      let paymentNumberNull = !!this.selectedResources.find((resource) => !resource.attributes.payment_confirmation_number)
      if (paymentNumberNull) {
        this.createNotification('Error', 'Payment Confirmation Number is required for all selected transactions to export.', 'warning', null, null, null, 5000)
      }
      return paymentNumberNull
    },
    handleClose () {
      this.pdfLoader = false
    },
    async printCopyMethod () {
      this.loadingRelated = true
      // Get the selected payment info from Vuex store
      this.paymentInfo = this.filteredselectedResources.map(item => ({ ...item.attributes }))
      // Arrays to store IDs based on payment type
      let paymentIn = []
      let accountPayableIn = []
      let checksIn = []
        // Loop through the paymentInfo array to categorize payment types
      this.paymentInfo.forEach((ap) => {
        if (ap.payment_type !== 'CHECK') {
          paymentIn.push(ap.id)
          accountPayableIn.push(ap.id)
        } else {
          checksIn.push(ap.id)
        }
      })
        // Construct the payload to pass to the API
      const newPayload = {
        payment__in: paymentIn,
        checks__in: checksIn,
        accountpayable__in: accountPayableIn
      }
      // Dispatch an action to cache the parameters
      const resp1 = await this.$store.dispatch('cacheParams', { data: newPayload })
      // Call the function to create the PDF
      this.createCheckrunPdf(resp1)
    },
    async createCheckrunPdf (resp) {
      try {
        const bank = this.getBankById(this.selectedBank.id)
        let payload = {}
        // Check if there is payment info and if the bank has a check-preferred template
        if (bank && bank.check_preferred_template) {
          let opts = await this.$store.dispatch('fetchOptions', { endpoint: 'reports/checks' })
          if (opts && opts.template_parameter && this.paymentInfo.length > 0) {
            // Get the payment type of the first item in the paymentInfo array
            const paymentType = this.paymentInfo[0].payment_type
            // Determine the template based on the payment type
            if (['EFT', 'WIRE', 'CHECK', 'CHECK_MANUAL'].includes(paymentType)) {
              payload[opts.template_parameter] = this.getReportTemplate(paymentType)
            } else if (opts.templates && opts.templates[bank.check_preferred_template]) {
              payload[opts.template_parameter] = bank.check_preferred_template
            }
          }
        } else {
          // If no bank info, fallback to the first payment type
          const paymentType = this.paymentInfo[0].payment_type
          payload.report_template = this.getReportTemplate(paymentType)
        }
        payload.cached_parameters = resp.id
        // Generate the PDF
        const report = await this.$store.dispatch('buildPrintCopyrunPdf', payload)
        this.buildProgress = 100
        this.loadingRelated = false
        // Set the PDF data to be displayed
        setTimeout(() => {
          this.loadPdf = report.data // Set the new PDF data
          this.pdfLoader = true // Open the dialog
        }, 100)
      } catch (e) {
        console.error(e)
        this.loadingRelated = false
        if (!(e instanceof DOMException)) {
          this.$notify({
            title: 'Error',
            message: e.message || 'Error generating PDF',
            type: 'error'
          })
        }
      }
    },
    getReportTemplate (paymentType) {
      // Determine the appropriate template for each payment type
      const templates = {
        EFT: 'payment_receipt_eft',
        WIRE: 'payment_receipt_wire_transfer',
        CHECK: 'default',
        CHECK_MANUAL: 'payment_receipt_manual_check'
      }
      return templates[paymentType] || 'default'
    }
  },
  computed: {
    ...mapGetters({
      getReportFormFields: 'getReportFormFields',
      getBankById: 'getBankById',
      getVendorById: 'getVendorById'
    }),
    resources () {
      return {
        AccountsPayableResource,
        VendorResource
      }
    },
    achEnabled () {
      return !!this.$store.getters.getUiSettings['ACH_ENABLED']
    },
    width () {
      // Reducing by 3 % to handle sigma-frontend navbar
      return '100%'
    },
    height () {
      // Reducing by 3 % to handle sigma-frontend navbar
      return window.innerHeight * 0.63 + 'px'
    },
    selectedBank () {
      if (this.selectedCodes && !this.selectedCodes.bank__in) {
        return null
      } else if (this.selectedCodes && this.selectedCodes.bank__in) {
        const bank = this.getBankById(this.selectedCodes.bank__in)
        return bank
      }
    },
    isNotPrintable () {
      // Disable the button only if no row has payment_type "EFT" or "Wire"
      return !this.rowData.some((rowData) =>
        rowData.attributes.payment_type === 'EFT' ||
        rowData.attributes.payment_type === 'Wire'
      )
    }
  },

  // Load bank
  async created () {
    this.loading = true
    let response = await store.dispatch('fetchBanks')
    if (response.banks && typeof response.banks === 'object') {
      for (const i in response.banks) {
        this.allCodes.bank__in.push(response.banks[i])
      }
      if (this.allCodes.bank__in.length === 1) {
        this.selectedCodes.bank__in = (this.allCodes.bank__in[0] || {}).id || null
        this.updateFormat()
      }
    } else {
      throw new Error('Unable to fetch report options')
    }
    await this.getTransactions()
    this.loading = false
    this.disableReportRun = false
  },
  mounted () {
    this.setPaymentBank()
  },
  watch: {
    paymentBank () {
      this.setPaymentBank()
    }
  }
})
</script>

<template>
  <div style="width: 100%;" v-loading.body="loading" element-loading-text="Loading...">
    <el-row type="flex" align="middle" justify="end" style="border-bottom: 1px solid lightgrey;">
      <HeaderInfoText :colspan="12" title="ACH Export" />
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <el-button v-if="step > 2 && step < 4" type="default" @click="step--" :disabled="disableReportRun"
            style="margin: 0 15px;">Back</el-button>
          <el-button v-if="step === 4" type="danger" plain @click="printCopyMethod" :disabled="isNotPrintable" :loading="!!loadingRelated">
            {{ !!loadingRelated ? 'Loading' : 'Print Copy' }}
          </el-button>
          <el-button v-if="step === 4" type="default" @click="onNotify" :disabled="disableReportRun"
            style="margin: 0 15px;">Notify Vendors</el-button>
          <el-button v-if="step === 4" type="default" @click="generatePaymentReport" :disabled="disableReportRun"
            style="margin: 0 15px;">Payment Register</el-button>
          <el-button v-if="step === 4" type="default" @click="resetPage" :disabled="disableReportRun"
            style="margin: 0 15px;">Finish</el-button>
          <el-button v-if="step === 1" plain type="primary" ref="getTransactions" @click="getTransactions" :disabled="disableReportRun"
            style="margin: 0 15px;">Get Available Transactions</el-button>
          <el-button :loading="validatingDuplicates" v-else-if="step === 2" type="primary" @click="goToConfirmationPage" :disabled="disableReportRun"
            style="margin: 0 15px;">Go to Confirmation Page</el-button>
          <el-button v-else-if="step === 3 && achEnabled" type="primary" @click="onGenerateExport" :disabled="disableReportRun"
            style="margin: 0 15px;">Create Export</el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle" justify="end" class="headerRow">
      <el-col>
        <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
          Export Options
        </el-row>
      </el-col>
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <p><sup>*</sup> Denotes a required field</p>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle">
      <el-col :span="12">
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            <div style="display: inline-flex">
              Bank Account<sup>*</sup>
            </div>
          </el-col>
          <el-col :span="9">
            <el-select v-model="selectedCodes.bank__in" :multiple="false" placeholder="Bank Account"
              @change="updateFormat" :disabled="!!paymentBank" required>
              <el-option v-for="opt in allCodes.bank__in" :key="opt.id" :label="opt.name" :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="9">
            <div v-if="selectedBank && !(selectedBank.branch_number)">
              &nbsp;Bank is not setup
            </div>
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Format<sup>*</sup>
          </el-col>
          <el-col :span="9">
            <el-select v-model="reportOptions.format" required>
              <el-option v-for="opt in reportOutputFormats" :key="opt.value" :label="opt.label" :value="opt.value" :disabled="opt.disabled">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow" v-if="step >= 3">
          <el-col :span="6">
            Payment Date<sup>*</sup>
          </el-col>
          <el-col :span="9">
            <input
              name="date"
              class="date-field"
              autocomplete="off"
              :style="cellStyle"
              :disabled="step > 3"
              type="text"
              required="true"
              placeholder="Payment(yyyy-mm-dd)"
              v-model="reportOptions.payment_date"
              @blur="validatePaymentDate"
            />
          </el-col>
          <el-col :span="9">
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Summary
          </el-col>
          <el-col :span="9">
            Amount Available to Export: <br /><strong>{{ intlNumberFormatter(totalAvailable || 0) }}</strong>
          </el-col>
          <el-col :span="9">
            Amount Selected to Export: <br /><strong>{{ intlNumberFormatter(totalSelected || 0) }}</strong>
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow" v-if="step < 3">
          <el-col :span="6" style="font-size: 0.8em;">
            First available payment number:
          </el-col>
          <el-col :span="3">
            <el-input
              :name="`payment_number_start`"
              size="small"
              placeholder="Payment number"
              v-model="startingPaymentConfNumber"
              @change="updatePaymentConfNumbersOnStep2">
            </el-input>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <el-row class="m-top-20">
      <div v-if="step === 3">
        <span class="m-left-10 bold red">Please confirm that the information below is correct. If you create an export,
          these transactions will be marked as processed.</span>
      </div>
      <div v-else-if="step === 4">
        <span class="m-left-10 bold blue">Please download your file and upload to the bank. Afterward, please update the
          payment confirmation numbers below with the bank's transaction numbers.</span>
      </div>
    </el-row>
    <el-row class="m-top-20">
      <el-col>
        <nacha-payment-conf-list v-if="step >= 3" name="nacha_payment_conf_list" ref="nacha_payment_conf_list"
          :allow-edit="step >= 3" :allow-notify="step < 4" :width="width" :height="height" :row-data="simulatedTransactions" @updatePaymentNumber="updatePaymentNumber" @onSelect="handleSelected" @updateCCMail="handleCellUpdate" />
        <nacha-transactions-list v-if="step < 3 && rowData.length" name="nacha_transactions_list" ref="nacha_transactions_list"
          :key="iteration" :width="width" :height="height" :row-data="rowData" :format="reportOptions.format"
          @update-totals="onUpdateTotals" @ach-errors="onAchErrors" />
      </el-col>
    </el-row>
    <el-row v-if="showJson" class="m-top-50">
      <el-col>
        <textarea id="output" style="width: 100%; height: 500px;" v-model="prettyJson"></textarea>
      </el-col>
    </el-row>
    <el-dialog
    title="Report Viewer"
    size="large"
    :show-close="true"
    :close-on-click-modal="true"
    :destroy-on-close="true"
    @close="handleClose"
    @before-close="handleBeforeClose"
    :fullscreen.sync="isFullscreen"
    :visible.sync="pdfLoader"
    :modal="false"
    :style="{ zIndex: 3000 }"
    top="2%">
      <template v-if="loadPdf">
        <el-row type="flex" align="middle" >
          <object :data="`data:application/pdf;base64,${loadPdf}`" type="application/pdf"  width="1500" height="600" ></object>
        </el-row>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-right-20 { margin-right: 20px; }
  .m-left-10 { margin-left: 10px; }
  .m-left-20 { margin-left: 20px; }
  .m-top-20 { margin-top: 20px; }
  .m-top-50 { margin-top: 50px; }
  .date {
    display: inline-flex;
  }
  .field {
    margin: 0 6px;
  }
  textarea {
    font: 10px;
    font-family: monospace;
  }
  .date-field {
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #bfcbd9;
    box-sizing: border-box;
    color: #1f2d3d;
    font-size: inherit;
    height: 36px;
    line-height: 1;
    outline: 0;
    padding: 3px 10px;
    transition: border-color .2s cubic-bezier(.645,.045,.355,1);
    width: 100%;
  }
  .el-overlay {
    z-index: 3000 !important;
  }
</style>
<style>
.el-select {
  display: block;
}

.el-transfer-panel {
  width: 36%;
  max-width: 350px
}

.el-transfer-panel__body {
  min-height: 400px
}

.el-transfer-panel__list {
  min-height: 360px
}

.form-error {
  background-color: #F08080;
  font-weight: bold;
  color: #000000;
}

/* border: 1px solid #d1dbe5; box-shadow: 0 2px 4px rgba(0,0,0,.12),0 0 6px rgba(0,0,0,.04); display: inline-block; width: 30%; position: relative; height: 500px; */



// WEBPACK FOOTER //
// src/components/Programs/NachaExport.vue